import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Discussions } from '@air/api';
import { type Discussion } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type ClipDiscussionsCacheType } from '~/swr-hooks/discussions/types';
import { isClipDiscussionsKey, updateDiscussionInCache } from '~/swr-hooks/discussions/utils';

type ResolveDiscussionParams = {
  boardId?: string;
  shortId?: string;
} & Pick<Required<Discussion>, 'id' | 'resolved' | 'assetId'>;

export const useResolveDiscussion = () => {
  const queryClient = useQueryClient();
  const resolveDiscussion = useCallback(
    async ({ id, resolved, shortId, boardId, assetId }: ResolveDiscussionParams) => {
      try {
        const queryToUpdate = ({ queryKey }: { queryKey: QueryKey }) =>
          isClipDiscussionsKey(queryKey) && queryKey[1].assetId === assetId;

        queryClient.setQueriesData<ClipDiscussionsCacheType>(
          {
            predicate: queryToUpdate,
          },
          (data) => updateDiscussionInCache({ id, resolved }, data),
        );

        // boardId is optional — backend accepts resolves without board context (e.g. standalone clip routes).
        await Discussions.update({
          input: {
            id,
            resolved,
          },
          // This is not used anymore since we changed comments to global
          boardId: '',
          shortId,
        });

        queryClient.invalidateQueries({ predicate: queryToUpdate });
      } catch (error) {
        throw reportErrorToBugsnag({
          error,
          context: 'Failed to resolve discussion',
          metadata: { data: { id, shortId, boardId } },
        });
      }
    },
    [queryClient],
  );

  return {
    resolveDiscussion,
  };
};
